/**
 * PhdDashboard - Landing page for Flux PhD
 *
 * Shows model status, recent benchmarks, quick actions, and forecasting status.
 * No project required — PhD is the central intelligence platform.
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    Science as ScienceIcon,
    PlayArrow as RunIcon,
    ModelTraining as TrainIcon,
    Assessment as BenchmarkIcon,
    TrendingUp as ForecastIcon,
    Psychology as LearningIcon,
} from '@mui/icons-material';
import { vortxTypography, useRetroContextSafe } from '@vortx/shared-ui';
import { useBenchmarkRuns } from '../../hooks';
import { COLORS, fadeInUp } from './results/ResultsDesignTokens';

const PHD_API_BASE = process.env.REACT_APP_PHD_API_URL || 'http://localhost:5002';

function StatusCard({ title, icon: Icon, children, delay = 0 }) {
    return (
        <Box sx={{
            flex: '1 1 300px',
            bgcolor: COLORS.bg.card,
            border: `1px solid ${alpha(COLORS.accent, 0.15)}`,
            borderRadius: 2,
            p: 2.5,
            animation: `${fadeInUp} 0.4s ease-out ${delay}s both`,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Icon sx={{ fontSize: 18, color: COLORS.accent }} />
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: COLORS.text.muted,
                }}>
                    {title}
                </Typography>
            </Box>
            {children}
        </Box>
    );
}

function MetricRow({ label, value, color }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.7rem',
                color: COLORS.text.secondary,
            }}>
                {label}
            </Typography>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: color || COLORS.text.primary,
            }}>
                {value}
            </Typography>
        </Box>
    );
}

function getPopColor(pop) {
    if (pop == null) return '#666';
    if (pop >= 85) return '#5fd4c4';
    if (pop >= 70) return COLORS.accent;
    return '#ff4466';
}

export default function PhdDashboard() {
    const { setPhdSection } = useRetroContextSafe();
    const { runs, loading: runsLoading } = useBenchmarkRuns({ maxResults: 3 });
    const [modelStatus, setModelStatus] = useState(null);
    const [modelLoading, setModelLoading] = useState(true);

    useEffect(() => {
        async function fetchModelStatus() {
            try {
                const response = await fetch(`${PHD_API_BASE}/api/coordination/policy-model/status`);
                if (response.ok) {
                    const data = await response.json();
                    setModelStatus(data);
                }
            } catch {
                // Model status unavailable — non-fatal
            } finally {
                setModelLoading(false);
            }
        }
        fetchModelStatus();
    }, []);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200 }}>
            {/* Header */}
            <Box sx={{ mb: 4, animation: `${fadeInUp} 0.3s ease-out` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <ScienceIcon sx={{ fontSize: 28, color: COLORS.accent }} />
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontSize: '1.4rem',
                        fontWeight: 700,
                        color: COLORS.accent,
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        textShadow: `0 0 20px ${alpha(COLORS.accent, 0.4)}`,
                    }}>
                        Flux PhD
                    </Typography>
                </Box>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.75rem',
                    color: COLORS.text.muted,
                    pl: 5.5,
                }}>
                    Central intelligence — benchmarks, model training, forecasting
                </Typography>
            </Box>

            {/* Cards grid */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 3 }}>
                {/* Model Status */}
                <StatusCard title="Policy Model" icon={TrainIcon} delay={0.05}>
                    {modelLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={20} sx={{ color: COLORS.accent }} />
                        </Box>
                    ) : modelStatus?.loaded ? (
                        <>
                            <MetricRow label="Version" value={modelStatus.version || 'v1'} color={COLORS.accent} />
                            <MetricRow label="Features" value={modelStatus.n_features || '--'} />
                            {modelStatus.rmse && (
                                <MetricRow label="RMSE" value={`${modelStatus.rmse.toFixed(3)}`} color="#5fd4c4" />
                            )}
                            {modelStatus.trained_at && (
                                <MetricRow
                                    label="Trained"
                                    value={new Date(modelStatus.trained_at).toLocaleDateString()}
                                />
                            )}
                        </>
                    ) : (
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: COLORS.text.muted,
                            textAlign: 'center',
                            py: 1,
                        }}>
                            No model loaded
                        </Typography>
                    )}
                </StatusCard>

                {/* Recent Benchmarks */}
                <StatusCard title="Recent Benchmarks" icon={BenchmarkIcon} delay={0.1}>
                    {runsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={20} sx={{ color: COLORS.accent }} />
                        </Box>
                    ) : runs.length === 0 ? (
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: COLORS.text.muted,
                            textAlign: 'center',
                            py: 1,
                        }}>
                            No benchmark runs yet
                        </Typography>
                    ) : (
                        runs.map((run) => {
                            const pop = run.summary?.mean_cost_pop_pct;
                            return (
                                <Box key={run.run_id} sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 0.5,
                                    borderBottom: `1px solid ${alpha(COLORS.accent, 0.08)}`,
                                    '&:last-child': { borderBottom: 'none' },
                                }}>
                                    <Box>
                                        <Typography sx={{
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.7rem',
                                            color: COLORS.text.primary,
                                        }}>
                                            {run.label || run.run_id.slice(0, 8)}
                                        </Typography>
                                        <Typography sx={{
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.6rem',
                                            color: COLORS.text.muted,
                                        }}>
                                            {run.submitted_at
                                                ? new Date(run.submitted_at).toLocaleDateString()
                                                : '--'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography sx={{
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.75rem',
                                            fontWeight: 700,
                                            color: run.status === 'completed'
                                                ? getPopColor(pop)
                                                : run.status === 'running' ? '#2196F3' : '#888',
                                        }}>
                                            {run.status === 'completed' && pop != null
                                                ? `${pop.toFixed(1)}%`
                                                : run.status.toUpperCase()}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })
                    )}
                </StatusCard>

                {/* Forecasting Status */}
                <StatusCard title="Forecasting" icon={ForecastIcon} delay={0.15}>
                    {modelLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={20} sx={{ color: COLORS.accent }} />
                        </Box>
                    ) : (
                        <>
                            <MetricRow
                                label="Model"
                                value={modelStatus?.loaded ? (modelStatus.version || 'Active') : 'None'}
                                color={modelStatus?.loaded ? '#5fd4c4' : COLORS.text.muted}
                            />
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                color: COLORS.text.muted,
                                mt: 1,
                            }}>
                                ComStock-based load forecasting with component decomposition
                            </Typography>
                        </>
                    )}
                </StatusCard>
            </Box>

            {/* Quick Actions */}
            <Box sx={{
                display: 'flex',
                gap: 2,
                animation: `${fadeInUp} 0.4s ease-out 0.2s both`,
            }}>
                <Button
                    variant="outlined"
                    startIcon={<RunIcon />}
                    onClick={() => setPhdSection('research')}
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: COLORS.accent,
                        borderColor: alpha(COLORS.accent, 0.4),
                        borderRadius: 1,
                        px: 3,
                        '&:hover': {
                            borderColor: COLORS.accent,
                            bgcolor: alpha(COLORS.accent, 0.08),
                        },
                    }}
                >
                    Run Benchmark
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<LearningIcon />}
                    onClick={() => setPhdSection('learning')}
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: COLORS.accent,
                        borderColor: alpha(COLORS.accent, 0.4),
                        borderRadius: 1,
                        px: 3,
                        '&:hover': {
                            borderColor: COLORS.accent,
                            bgcolor: alpha(COLORS.accent, 0.08),
                        },
                    }}
                >
                    Learning
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<ForecastIcon />}
                    onClick={() => setPhdSection('forecasting')}
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: COLORS.accent,
                        borderColor: alpha(COLORS.accent, 0.4),
                        borderRadius: 1,
                        px: 3,
                        '&:hover': {
                            borderColor: COLORS.accent,
                            bgcolor: alpha(COLORS.accent, 0.08),
                        },
                    }}
                >
                    Forecasting
                </Button>
            </Box>
        </Box>
    );
}
